// apps/web/src/components/onboarding/OnboardingQuiz.tsx
// The logged-out Get Started wizard. Collects every answer into local state
// (mirrored to localStorage so it survives the OAuth round-trip), shows the
// computed plan, then creates the account — after which the post-auth gate
// submits the held answers. Layout per owner spec: progress bar on top, title
// top-left + subtitle, Continue disabled until the step is answered.
import { useState } from "react";
import { api } from "../../lib/api";
import { useT, apiLang, useLocale } from "../../lib/i18n";
import { Button } from "../ui/Button";
import { WheelDatePicker } from "./WheelDatePicker";
import { SocialButton } from "../SocialAuthButtons";
import { EmailCodeAuth } from "../EmailCodeAuth";
import { isNativeApp } from "../../lib/native";
import { nativeAppleSignIn } from "../../lib/nativeApple";
import { nativeBrowserOAuth } from "../../lib/nativeOAuth";
import { savePending, type PendingOnboarding } from "../../lib/pendingOnboarding";
import { RulerPicker } from "./RulerPicker";
import { hapticSelect } from "../../lib/haptics";
import { cmToIn, inToCm, kgToLb, lbToKg } from "@shared/units";
import { initialTargets, type ActivityLevel } from "@shared/onboarding";
import { eventPacing, paceOutlook, daysBetween, MIN_EVENT_DAYS, MAX_EVENT_DAYS } from "@shared/event-goal";
import { minHealthyWeightKg, maxSensibleWeightKg } from "@shared/healthy-weight";
import { ABSOLUTE_MIN_KCAL, MAX_MANUAL_KCAL } from "@shared/safety-floors";
import { todayLocal } from "../../lib/date";
import { useEffect } from "react";
import { Flame, Scale, Dumbbell, Users, Tv, Music2, MoreHorizontal, type LucideIcon } from "lucide-react";
import type { Sex, GoalType } from "@shared/types";
import type { ReactNode } from "react";

type Provider = "apple" | "google";
const SEX_OPTIONS: Sex[] = ["male", "female", "other"];
const WORKOUTS: Array<{ id: ActivityLevel; key: string }> = [
  { id: "sedentary", key: "start.workouts_0" },
  { id: "light", key: "start.workouts_1_2" },
  { id: "moderate", key: "start.workouts_3_5" },
  { id: "high", key: "start.workouts_6" },
];
const GOAL_OPTIONS: Array<{ id: GoalType; key: string; Icon: LucideIcon; tint: string }> = [
  { id: "weight_loss", key: "ob.goalLose", Icon: Flame, tint: "#f97316" },
  { id: "maintain", key: "ob.goalMaintain", Icon: Scale, tint: "#38bdf8" },
  { id: "bulk", key: "ob.goalMuscle", Icon: Dumbbell, tint: "#a78bfa" },
];
const NUTRITION = ["standard", "pescatarian", "vegetarian", "vegan", "other"] as const;
const REFERRALS = ["friend", "app_store", "facebook", "youtube", "tv", "instagram", "x", "google", "tiktok", "other"] as const;
const EVENT_PRESETS = [
  { key: "ob.eventPreset_wedding", emoji: "💍" }, { key: "ob.eventPreset_vacation", emoji: "🏖️" },
  { key: "ob.eventPreset_birthday", emoji: "🎂" }, { key: "ob.eventPreset_party", emoji: "🎉" },
] as const;

const LABEL = "text-sm text-fg-muted uppercase tracking-wider";
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

function ageFromBirthday(iso: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return NaN;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const now = new Date();
  let age = now.getFullYear() - y;
  if (now.getMonth() + 1 < mo || (now.getMonth() + 1 === mo && now.getDate() < d)) age -= 1;
  return age;
}
function bmiOf(w: number, h: number) {
  const v = w / Math.pow(h / 100, 2);
  return { value: Math.round(v * 10) / 10, label: v < 18.5 ? "underweight" : v < 25 ? "normal" : v < 30 ? "overweight" : "obese" };
}


/** A choice chip grid (radio-style single-select). */
function ChipGrid({ options, value, onPick, cols = 2 }: {
  options: Array<{ id: string; label: string }>; value: string | null; onPick: (id: string) => void; cols?: number;
}) {
  return (
    <div className={`grid gap-2 ${cols === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
      {options.map((o) => (
        <button key={o.id} type="button" onClick={() => { hapticSelect(); onPick(o.id); }}
          className={`py-3.5 px-2 rounded-xl text-sm font-medium transition press ${
            value === o.id ? "bg-brand text-on-brand" : "bg-surface text-fg-secondary"
          }`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Two-pill unit switch (cm/ft-in, kg/lb). */
function UnitToggle({ options, value, onChange }: {
  options: Array<{ id: string; label: string }>; value: string; onChange: (id: string) => void;
}) {
  return (
    <div className="mx-auto flex w-fit rounded-full bg-surface p-1">
      {options.map((o) => (
        <button key={o.id} type="button" onClick={() => { hapticSelect(); onChange(o.id); }}
          className={`px-5 py-1.5 rounded-full text-sm font-semibold transition ${
            value === o.id ? "bg-brand text-on-brand" : "text-fg-muted"
          }`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

const AppleGlyph = () => (<svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.63-2.323-7.28 0-4.28 2.797-6.55 5.552-6.55 1.448 0 2.675.95 3.6.95.865 0 2.222-1.01 3.902-1.01.613 0 2.886.06 4.374 2.19-.13.09-2.383 1.37-2.383 4.19 0 3.26 2.854 4.42 2.954 4.45z"/></svg>);
const XGlyph = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.9 1.9h3.7l-8 9.1L24 22.1h-7.4l-5.8-7.6-6.6 7.6H.5l8.6-9.8L0 1.9h7.6l5.2 6.9 6.1-6.9zm-1.3 18h2L6.5 3.8h-2.2L17.6 19.9z"/></svg>);
const FacebookGlyph = () => (<svg width="17" height="17" viewBox="0 0 24 24" fill="#1877F2" aria-hidden="true"><path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z"/></svg>);
const YoutubeGlyph = () => (<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="#FF0000" d="M23.5 6.2a3 3 0 0 0-2.11-2.13C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.39.52A3 3 0 0 0 .5 6.2 31.3 31.3 0 0 0 0 12a31.3 31.3 0 0 0 .5 5.8 3 3 0 0 0 2.11 2.13c1.89.52 9.39.52 9.39.52s7.5 0 9.39-.52a3 3 0 0 0 2.11-2.13A31.3 31.3 0 0 0 24 12a31.3 31.3 0 0 0-.5-5.8z"/><path fill="#fff" d="M9.6 15.6V8.4l6.2 3.6z"/></svg>);
const InstagramGlyph = () => (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="2" y="2" width="20" height="20" rx="6" stroke="#E4405F" strokeWidth="2"/><circle cx="12" cy="12" r="4.5" stroke="#E4405F" strokeWidth="2"/><circle cx="17.5" cy="6.5" r="1.3" fill="#E4405F"/></svg>);
const GoogleGlyph = () => (<svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>);

const REFERRAL_ICONS: Record<string, JSX.Element> = {
  friend: <Users size={18} />, app_store: <AppleGlyph />, facebook: <FacebookGlyph />,
  youtube: <YoutubeGlyph />, tv: <Tv size={18} />, instagram: <InstagramGlyph />,
  x: <XGlyph />, google: <GoogleGlyph />, tiktok: <Music2 size={18} />, other: <MoreHorizontal size={18} />,
};

interface Props {
  /** "signup" (default): logged-out — create an account at the end.
   *  "authed": already signed in (a reset account) — submit directly. */
  mode?: "signup" | "authed";
  onAuthed?: () => void;
  onSubmit?: (p: PendingOnboarding) => Promise<void>;
  onExit: () => void;
}

export function OnboardingQuiz({ mode = "signup", onAuthed, onSubmit, onExit }: Props) {
  const t = useT();
  const { locale } = useLocale();
  const native = isNativeApp();

  const [step, setStep] = useState(0);
  const [sex, setSex] = useState<Sex | null>(null);
  const [activity, setActivity] = useState<ActivityLevel | null>(null);
  const [birthday, setBirthday] = useState("");
  const [height, setHeight] = useState("170");
  const [heightUnit, setHeightUnit] = useState<"cm" | "in">("cm");
  const [weight, setWeight] = useState("70");
  const [weightUnit, setWeightUnit] = useState<"kg" | "lb">("kg");
  const [goal, setGoal] = useState<GoalType | null>(null);
  const [target, setTarget] = useState("");
  const [nutrition, setNutrition] = useState<string>("");
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [referral, setReferral] = useState<string | null>(null);
  const [tried, setTried] = useState<boolean | null>(null);
  const [calAdj, setCalAdj] = useState<number | null>(null);
  const [providers, setProviders] = useState<Provider[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showEmail, setShowEmail] = useState(false);

  const ageN = ageFromBirthday(birthday), heightN = Number(height), weightN = Number(weight);
  const targetN = target === "" ? weightN : Number(target);
  const weightValid = weightN >= 30 && weightN <= 400;
  const targetValid = targetN >= 30 && targetN <= 400;
  const minHealthy = heightN >= 100 ? minHealthyWeightKg(heightN) : 0;
  const targetTooLow = goal === "weight_loss" && target !== "" && targetValid && targetN < minHealthy;
  const targetHighGain = goal === "bulk" && target !== "" && targetValid && heightN >= 100 && targetN > maxSensibleWeightKg(heightN);

  const evDays = eventDate ? daysBetween(todayLocal(), eventDate) : null;
  const eventOk = evDays === null || (evDays >= MIN_EVENT_DAYS && evDays <= MAX_EVENT_DAYS);
  const useEvent = goal !== "maintain" && goal !== null && eventDate !== "" && eventOk;
  const pacing = useEvent && weightValid && targetValid
    ? eventPacing({ weight_kg: weightN, target_weight_kg: targetN, event_date: eventDate, today: todayLocal(), goal_type: goal! })
    : null;
  const plan = sex && goal && ageN >= 16 && heightN >= 100 && weightValid
    ? initialTargets({ sex, age: ageN, height_cm: heightN, weight_kg: weightN, goal_type: goal, activity: activity ?? undefined },
        useEvent ? { event_date: eventDate, today: todayLocal(), target_weight_kg: targetN } : null)
    : null;
  const bmi = heightN >= 100 && weightValid ? bmiOf(weightN, heightN) : null;
  const calVal = calAdj ?? plan?.daily_calories ?? 0;
  const protVal = plan?.protein_g ?? 0;

  // The event step only exists for non-maintain goals.
  const hasEvent = goal !== "maintain";

  type Q = { key: string; title: string; sub?: string; body: ReactNode; ok: boolean };
  const questions: Q[] = [
    { key: "sex", title: t("start.q_sex_title"), sub: t("start.q_sex_sub"), ok: sex !== null,
      body: <ChipGrid cols={3} value={sex} onPick={(v) => setSex(v as Sex)}
        options={SEX_OPTIONS.map((id) => ({ id, label: t(`sex.${id}`) }))} /> },
    { key: "workouts", title: t("start.q_workouts_title"), sub: t("start.q_workouts_sub"), ok: activity !== null,
      body: <div className="space-y-2">{WORKOUTS.map((w) => (
        <button key={w.id} type="button" onClick={() => { hapticSelect(); setActivity(w.id); }}
          className={`w-full py-3.5 rounded-xl text-sm font-medium press ${activity === w.id ? "bg-brand text-on-brand" : "bg-surface text-fg-secondary"}`}>
          {t(w.key)}
        </button>))}</div> },
    { key: "birthday", title: t("start.q_birthday_title"), sub: t("start.q_birthday_sub"), ok: Number.isFinite(ageN) && ageN >= 16 && ageN <= 120,
      body: <>
        <WheelDatePicker value={birthday} onChange={setBirthday}
          minYear={new Date().getFullYear() - 100} maxYear={new Date().getFullYear() - 13}
          fallbackYear={new Date().getFullYear() - 25} />
        {birthday && Number.isFinite(ageN) && (
          <p className="mt-3 text-center text-sm text-fg-muted">{t("ob.age_is", { n: String(ageN) })}</p>)}
        {birthday && Number.isFinite(ageN) && ageN < 16 && (
          <p className="mt-2 text-center text-sm text-danger">{t("start.age_min")}</p>)}
      </> },
    { key: "height", title: t("start.q_height_title"), sub: t("start.q_height_sub"), ok: heightN >= 100 && heightN <= 250,
      body: (() => {
        const inMode = heightUnit === "in";
        const disp = inMode ? Math.round(cmToIn(heightN)) : heightN;
        const label = inMode ? `${Math.floor(disp / 12)}'${disp % 12}"` : `${heightN} cm`;
        return (
          <div className="space-y-6">
            <div className="text-center text-5xl font-bold tabular-nums">{label}</div>
            <RulerPicker min={inMode ? 48 : 120} max={inMode ? 87 : 220} step={1} value={disp}
              majorEvery={inMode ? 12 : 10}
              onChange={(v) => setHeight(String(inMode ? Math.round(inToCm(v)) : v))} />
            <UnitToggle value={heightUnit} onChange={(u) => setHeightUnit(u as "cm" | "in")}
              options={[{ id: "cm", label: "cm" }, { id: "in", label: "ft / in" }]} />
          </div>
        );
      })() },
    { key: "weight", title: t("start.q_weight_title"), sub: t("start.q_weight_sub"), ok: weightValid,
      body: (() => {
        const lb = weightUnit === "lb";
        const disp = lb ? Math.round(kgToLb(weightN)) : Math.round(weightN);
        return (
          <div className="space-y-6">
            <div className="text-center text-5xl font-bold tabular-nums">{disp} <span className="text-2xl text-fg-muted">{lb ? "lb" : "kg"}</span></div>
            <RulerPicker min={lb ? 66 : 30} max={lb ? 440 : 200} step={1} value={disp}
              onChange={(v) => setWeight(String(lb ? Math.round(lbToKg(v) * 10) / 10 : v))} />
            <UnitToggle value={weightUnit} onChange={(u) => setWeightUnit(u as "kg" | "lb")}
              options={[{ id: "kg", label: "kg" }, { id: "lb", label: "lb" }]} />
          </div>
        );
      })() },
    { key: "goal", title: t("start.q_goal_title"), sub: t("start.q_goal_sub"),
      ok: goal !== null && (goal === "maintain" || (targetValid && !targetTooLow)),
      body: <div className="space-y-5">
        <div className="grid grid-cols-3 gap-2">
          {GOAL_OPTIONS.map(({ id, key, Icon, tint }) => (
            <button key={id} type="button" onClick={() => { hapticSelect(); setGoal(id); if (id !== "maintain" && target === "") setTarget(String(id === "weight_loss" ? Math.max(30, Math.round(weightN - 5)) : Math.round(weightN + 5))); }}
              className={`flex flex-col items-center gap-2 py-3.5 rounded-2xl border transition press ${goal === id ? "border-brand bg-brand/10" : "border-line bg-surface"}`}>
              <span aria-hidden="true" className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: tint }}>
                <Icon size={19} strokeWidth={2.2} className="text-black/80" />
              </span>
              <span className="text-sm leading-tight text-center">{t(key)}</span>
            </button>))}
        </div>
        {goal && goal !== "maintain" && (() => {
          const lb = weightUnit === "lb";
          const disp = lb ? Math.round(kgToLb(targetN)) : Math.round(targetN);
          return (
            <div className="space-y-2">
              <p className="text-center text-sm text-fg-muted">{t("ob.targetWeight")}</p>
              <div className="text-center text-4xl font-bold tabular-nums">{disp} <span className="text-xl text-fg-muted">{lb ? "lb" : "kg"}</span></div>
              <RulerPicker min={lb ? 66 : 30} max={lb ? 440 : 200} step={1} value={disp}
                onChange={(v) => setTarget(String(lb ? Math.round(lbToKg(v) * 10) / 10 : v))} />
            </div>
          );
        })()}
        {targetTooLow && <div className="rounded-2xl border border-danger/40 bg-danger/10 px-4 py-3 text-sm leading-relaxed">{t("ob.targetTooLow", { height: String(heightN), min: String(minHealthy) })}</div>}
        {targetHighGain && <div className="rounded-2xl border border-warn/40 bg-warn/10 px-4 py-3 text-sm leading-relaxed">{t("ob.targetHighGain", { height: String(heightN) })}</div>}
      </div> },
    { key: "nutrition", title: t("start.q_nutrition_title"), sub: t("start.q_nutrition_sub"), ok: true,
      body: <div className="flex flex-wrap gap-2">
        {NUTRITION.map((id) => (
          <button key={id} type="button" onClick={() => { hapticSelect(); setNutrition(nutrition === id ? "" : id); }}
            className={`px-3.5 py-2 rounded-full text-sm font-medium press border transition-colors ${nutrition === id ? "bg-brand text-on-brand border-brand" : "bg-surface border-line text-fg-secondary"}`}>
            {t(`nutrition.${id}`)}
          </button>))}
      </div> },
    ...(hasEvent ? [{ key: "event", title: t("ob.eventTitle"), sub: t("ob.eventSub"), ok: eventOk,
      body: <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {EVENT_PRESETS.map(({ key, emoji }) => {
            const label = t(key); const active = eventName === label;
            return <button key={key} type="button" onClick={() => { hapticSelect(); setEventName(active ? "" : label); }}
              className={`px-3.5 py-2 rounded-full text-sm font-medium press border transition-colors ${active ? "bg-brand text-on-brand border-brand" : "bg-surface border-line text-fg-secondary"}`}>
              {emoji} {label}</button>;
          })}
        </div>
        <input className="input" value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder={t("ob.eventName_ph")} maxLength={60} />
        <label className="block">
          <span className={LABEL}>{t("ob.eventDate_label")}</span>
          <input className="input mt-1.5 text-center text-lg" type="date" value={eventDate}
            onChange={(e) => setEventDate(e.target.value)} />
        </label>
        {eventDate && !eventOk && <p className="text-center text-sm text-danger">{t("ob.eventRange")}</p>}
        {pacing && <div className={`rounded-2xl border px-4 py-3 text-sm leading-relaxed ${pacing.limited ? "border-warn/40 bg-warn/10" : "border-brand/40 bg-brand/10"}`}>
          {pacing.limited ? t("ob.eventTight", { weeks: String(pacing.weeks), delta: String(Math.round(Math.abs(targetN - weightN) * 10) / 10), achievable: String(paceOutlook(targetN - weightN, pacing.days).achievableKg), needWeeks: String(paceOutlook(targetN - weightN, pacing.days).neededWeeks) }) : t("ob.eventPace", { weeks: String(pacing.weeks) })}
        </div>}
      </div> } as Q] : []),
    { key: "referral", title: t("start.q_referral_title"), sub: t("start.q_referral_sub"), ok: referral !== null,
      body: <div className="grid grid-cols-2 gap-2.5">
        {REFERRALS.map((id) => (
          <button key={id} type="button" onClick={() => { hapticSelect(); setReferral(id); }}
            className={`flex items-center gap-3 px-3 py-3.5 rounded-2xl border transition press ${referral === id ? "border-brand bg-brand/10" : "border-line bg-surface"}`}>
            <span aria-hidden="true" className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-surface-strong">{REFERRAL_ICONS[id]}</span>
            <span className="text-sm font-medium text-fg-secondary text-left leading-tight">{t(`start.referral_${id}`)}</span>
          </button>))}
      </div> },
    { key: "tried", title: t("start.q_tried_title"), sub: t("start.q_tried_sub"), ok: tried !== null,
      body: <div className="grid grid-cols-2 gap-2">
        {[{ v: true, k: "common.yes" }, { v: false, k: "common.no" }].map(({ v, k }) => (
          <button key={String(v)} type="button" onClick={() => { hapticSelect(); setTried(v); }}
            className={`py-3.5 rounded-xl text-sm font-medium press ${tried === v ? "bg-brand text-on-brand" : "bg-surface text-fg-secondary"}`}>
            {t(k)}</button>))}
      </div> },
  ];

  const total = questions.length + 1; // + the plan/account finale
  const isFinal = step === questions.length;
  const progress = (step + 1) / total;

  const [submitting, setSubmitting] = useState(false);

  function buildAnswers(): PendingOnboarding | null {
    if (!sex || !goal) return null;
    return {
      name: undefined, sex, activity: activity ?? undefined,
      birthday: birthday || undefined, age: ageN,
      height_cm: heightN, weight_kg: weightN,
      target_weight_kg: goal === "maintain" ? weightN : targetN, goal_type: goal,
      nutrition_pref: nutrition || undefined,
      event_name: useEvent && eventName.trim() ? eventName.trim() : undefined,
      event_date: useEvent ? eventDate : undefined,
      referral_source: referral ?? undefined,
      tried_tracking: tried ?? undefined,
      daily_calories: calVal || undefined, protein_g: protVal || undefined,
      measured_on: todayLocal(),
    };
  }
  // Mirror answers to localStorage so they survive the OAuth round-trip (signup).
  function persist() { const a = buildAnswers(); if (a) savePending(a); }

  useEffect(() => {
    if (!isFinal || mode !== "signup") return;
    persist(); // hold answers the moment the plan shows — survives the OAuth round-trip
    let cancelled = false;
    api.authProviders().then((r) => { if (!cancelled) setProviders(r.providers); }).catch(() => { if (!cancelled) setProviders([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFinal]);

  // Re-hold if the plan's calories are nudged before auth (signup only).
  useEffect(() => { if (isFinal && mode === "signup") persist(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calAdj]);

  // authed mode: already signed in — write the answers straight to the account.
  async function submitAuthed() {
    const a = buildAnswers();
    if (!a || !onSubmit) return;
    setSubmitting(true); setErr(null);
    try { await onSubmit(a); }
    catch { setErr(t("start.save_failed")); setSubmitting(false); }
  }

  async function appleNative() {
    setErr(null); persist();
    try { await nativeAppleSignIn(apiLang(locale)); onAuthed?.(); }
    catch { setErr(t("auth.oauthError")); }
  }
  async function googleNative() {
    setErr(null); persist();
    try { await nativeBrowserOAuth("google"); onAuthed?.(); }
    catch (e) { if (!(e instanceof Error && e.message === "cancelled")) setErr(t("auth.oauthError")); }
  }

  function back() {
    if (step === 0) { onExit(); return; } // signup → landing; authed → welcome intro
    setStep(step - 1);
  }

  return (
    <div className="page-scroll min-h-screen-d bg-canvas text-fg flex flex-col">
      {/* progress bar + back */}
      <div className="flex items-center gap-3 px-5 pt-safe pt-4">
        <button type="button" onClick={back} aria-label={t("common.back")} className="text-fg-secondary text-2xl leading-none -ml-1 press min-w-[36px] text-left">‹</button>
        <div className="flex-1 h-1.5 rounded-full bg-surface-strong overflow-hidden">
          <div className="h-full rounded-full bg-brand transition-[width] duration-300" style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
      </div>

      {isFinal ? (
        // Plan payoff + create account
        <div className="flex-1 flex flex-col px-6 pt-6 pb-safe">
          <h1 className="text-2xl font-bold tracking-tight">{t("start.plan_title")}</h1>
          <p className="text-fg-muted text-sm mt-1">{t("start.plan_sub")}</p>
          <div className="mt-5 flex-1">
            {plan && (
              <div className="rounded-2xl bg-surface p-6 text-center space-y-1">
                <div className="flex items-center justify-center gap-4">
                  <button type="button" className="press w-11 h-11 rounded-full bg-surface-strong text-xl" aria-label="−50"
                    onClick={() => { hapticSelect(); setCalAdj((p) => clamp((p ?? plan.daily_calories) - 50, ABSOLUTE_MIN_KCAL, MAX_MANUAL_KCAL)); }}>−</button>
                  <div className="text-5xl font-semibold tabular-nums min-w-[8rem]">{calVal}</div>
                  <button type="button" className="press w-11 h-11 rounded-full bg-surface-strong text-xl" aria-label="+50"
                    onClick={() => { hapticSelect(); setCalAdj((p) => clamp((p ?? plan.daily_calories) + 50, ABSOLUTE_MIN_KCAL, MAX_MANUAL_KCAL)); }}>+</button>
                </div>
                <div className="text-fg-muted text-sm">{t("ob.planPerDay")}</div>
                <div className="pt-4 grid grid-cols-2 gap-2">
                  <div><div className="text-sm font-medium tabular-nums">{protVal} g</div><div className="text-sm text-fg-muted">{t("ob.proteinPerDay")}</div></div>
                  {bmi && <div><div className="text-sm font-medium tabular-nums">{bmi.value}</div><div className="text-sm text-fg-muted">BMI · {t(`bmi.${bmi.label}`)}</div></div>}
                </div>
              </div>
            )}
            <p className="mt-4 text-fg-secondary text-sm leading-relaxed">{t("ob.honest_estimates")}</p>
          </div>

          <div className="mt-4 space-y-2">
            {err && <p className="text-sm text-danger text-center">{err}</p>}
            {mode === "authed" ? (
              // Already signed in — a single Start button writes the plan.
              <Button fullWidth disabled={submitting} onClick={submitAuthed}>
                {submitting ? t("common.loading") : t("start.start_tracking")}
              </Button>
            ) : (<>
              <p className="text-center text-sm font-medium text-fg-secondary mb-1">{t("start.create_to_save")}</p>
              {providers !== null && providers.length > 0 && (<>
                {providers.includes("apple") && (native
                  ? <SocialButton provider="apple" onClick={appleNative}>{t("auth.continueWithApple")}</SocialButton>
                  : <SocialButton provider="apple">{t("auth.continueWithApple")}</SocialButton>)}
                {providers.includes("google") && (native
                  ? <SocialButton provider="google" onClick={googleNative}>{t("auth.continueWithGoogle")}</SocialButton>
                  : <SocialButton provider="google">{t("auth.continueWithGoogle")}</SocialButton>)}
              </>)}
              {providers !== null && (providers.length === 0 || showEmail) && (
                <div onClick={persist}><EmailCodeAuth onAuthed={() => onAuthed?.()} /></div>)}
              {providers !== null && providers.length > 0 && !showEmail && (
                <button onClick={() => setShowEmail(true)} className="w-full text-center text-sm text-fg-muted press pt-1">{t("auth.loginWithEmail")}</button>)}
            </>)}
            <p className="text-center text-sm text-fg-faint leading-relaxed pt-1">{t("disclaimer.full")}</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col px-6 pt-6 pb-safe">
          <h1 className="text-2xl font-bold tracking-tight">{questions[step].title}</h1>
          {questions[step].sub && <p className="text-fg-muted text-sm mt-1 leading-relaxed">{questions[step].sub}</p>}
          <div className="mt-4 flex-1 overflow-y-auto">
            <div className="flex min-h-full flex-col justify-center pb-2">{questions[step].body}</div>
          </div>
          <Button fullWidth disabled={!questions[step].ok} onClick={() => setStep(step + 1)} className="mt-4">
            {t("common.next")}
          </Button>
        </div>
      )}
    </div>
  );
}
